export const calculateItemValues = (item) => {
    const quantity = parseFloat(item.quantity_received) || 0;
    const unitPrice = parseFloat(item.unit_price) || 0;
    const discountPercent = parseFloat(item.discount_percentage) || 0;
    
    // Calculate line amount
    const initialLineAmount = quantity * unitPrice;
    const discountAmount = (initialLineAmount * discountPercent) / 100;
    const lineAmount = initialLineAmount - discountAmount;
    
    return {
        line_amount: lineAmount.toFixed(3),
        discount_amount: discountAmount.toFixed(3)
    };
};

export const calculateExcludingVatTotal = (items) => {
    return items.reduce((total, item) => {
        return total + (parseFloat(item.amount_excl_vat) || 0);
    }, 0);
};

export const calculateGrossTotal = (items) => {
    return items.reduce((total, item) => {
        return total + (parseFloat(item.line_amount) || 0);
    }, 0);
};

export const calculateOverallDiscount = (grossTotal, discountPercent, discountAmount) => {
    if (discountPercent > 0) {
        return (grossTotal * discountPercent) / 100;
    }
    return parseFloat(discountAmount) || 0;
};

export const distributeHeaderDiscount = (items, overallDiscountAmount) => {
    const grossTotal = calculateGrossTotal(items);
    // if (grossTotal <= 0) return items;

    return items.map(item => {
        const lineAmount = parseFloat(item.line_amount) || 0;
        // const weight = lineAmount / grossTotal;
        const weight = (lineAmount <= 0) ? 0 : lineAmount / grossTotal;

        const headerDiscount = (overallDiscountAmount * weight).toFixed(3);
        
        // Calculate amount excluding VAT
        const amountExclVat = lineAmount - parseFloat(headerDiscount);

        // Calculate VAT
        const vatPercent = parseFloat(item.vat_percentage) || 0;
        const vatAmount = (amountExclVat * vatPercent) / 100;
        
        // Calculate amount including VAT
        const amountInclVat = amountExclVat + vatAmount;
        
        return {
            ...item,
            header_discount: headerDiscount,
            amount_excl_vat: amountExclVat.toFixed(3),
            vat_amount: vatAmount.toFixed(3),
            amount_incl_vat: amountInclVat.toFixed(3)
        };
    });
};

export const calculateTotals = (items) => {
    return items.reduce((acc, item) => {
        acc.grossTotal += parseFloat(item.line_amount) || 0;
        acc.vatAmount += parseFloat(item.vat_amount) || 0;
        acc.netTotal += parseFloat(item.amount_incl_vat) || 0;
        return acc;
    }, {
        grossTotal: 0,
        vatAmount: 0,
        netTotal: 0
    });
};

export const calculateTotalCharges = (additionalCharges, conversionRate = 1) => {
    return additionalCharges.reduce((total, charge) => {
        if (!charge.purchase_vat) {
            return total + (parseFloat(charge.amount) || 0);
        }
        return total;
    }, 0) / conversionRate;
};

export const distributeTotalCharges = (items, totalCharges) => {
    const excludingVatTotal = calculateExcludingVatTotal(items);
    // if (excludingVatTotal <= 0) return items;

    return items.map(item => {
        const amountExclVat = parseFloat(item.amount_excl_vat) || 0;
        const weight = (amountExclVat <= 0) ? 0 : amountExclVat / excludingVatTotal;
        const distributedCharge = (totalCharges * weight).toFixed(3);
        
        // Calculate landed line total
        const landedLineTotal = amountExclVat + parseFloat(distributedCharge);
        
        // Calculate landed cost
        const quantity = parseFloat(item.quantity_received) || 0;
        const landedCost = quantity > 0 ? (landedLineTotal / quantity).toFixed(3) : '0.000';
        
        return {
            ...item,
            landed_line_total: landedLineTotal.toFixed(3),
            landed_cost: landedCost
        };
    });
};

export const calculateFinalTotals = (items, additionalCharges, conversionRate = 1) => {
    const totals = calculateTotals(items);
    const totalCharges = calculateTotalCharges(additionalCharges, conversionRate);
    
    const supplierTotal = totals.netTotal;
    const totalCost = totalCharges + supplierTotal;

    return {
        ...totals,
        total_charges: totalCharges.toFixed(3),
        total_charges_lcy: (totalCharges * conversionRate).toFixed(3),
        supplier_total: supplierTotal.toFixed(3),
        supplier_total_lcy: (supplierTotal * conversionRate).toFixed(3),
        total_cost: totalCost.toFixed(3),
        total_cost_lcy: (totalCost * conversionRate).toFixed(3)
    };
};

export const calculateItemLandedCosts = (item, conversionRate = 1) => {
    const landedCost = parseFloat(item.landed_cost) || 0;
    const landedLineTotal = parseFloat(item.landed_line_total) || 0;

    return {
        ...item,
        landed_cost_lcy: (landedCost * conversionRate).toFixed(3),
        landed_line_total_lcy: (landedLineTotal * conversionRate).toFixed(3)
    };
};

export const roundLineLcy = (amountFcy, conversionRate = 1) => {
    const amount = parseFloat(amountFcy) || 0;
    if (amount <= 0) {
        return 0;
    }

    return Math.round(amount * conversionRate * 1000) / 1000;
};

export const summarizeLineLcyAmounts = (items, conversionRate = 1) => {
    return items.reduce((acc, item) => {
        acc.sub_total_lcy += roundLineLcy(item.amount_excl_vat, conversionRate);
        acc.vat_amount_lcy += roundLineLcy(item.vat_amount, conversionRate);
        acc.net_total_lcy += roundLineLcy(item.amount_incl_vat, conversionRate);
        return acc;
    }, {
        sub_total_lcy: 0,
        vat_amount_lcy: 0,
        net_total_lcy: 0
    });
};

export const calculateOverallAmounts = (grossTotal, discountAmount, vatAmount, netTotal, conversionRate = 1) => {
    return {
        gross_total_lcy: (grossTotal * conversionRate).toFixed(3),
        discount_amount_lcy: (discountAmount * conversionRate).toFixed(3),
        sub_total_lcy: ((grossTotal - discountAmount) * conversionRate).toFixed(3),
        vat_amount_lcy: (vatAmount * conversionRate).toFixed(3),
        net_total_lcy: (netTotal * conversionRate).toFixed(3)
    };
};